import React, { useEffect } from "react";
import { Modal } from "@pd/pd-ui-kit";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  isAssignFormFilled,
  isAssignOrganizationFilled,
  mapAssignFormToPayload,
} from "~/features/LicenseActionsModals/model/mapPayload";
import {
  IAssignOrganizationValue,
  ILicenseAssignFormValues,
} from "~/features/LicenseActionsModals/model/types";
import { useLicenseActionsSubmit } from "~/features/LicenseActionsModals/model/useLicenseActionsSubmit";
import { useURLParamsSetter } from "~/hooks/useURLParamsSetter";

import AddEntityLink from "./AddEntityLink";
import AssignOrganizationBlock, {
  createEmptyPosition,
} from "./AssignOrganizationBlock";

import s from "~/features/LicenseActionsModals/LicenseActionsModals.less";

interface LicenseAssignModalProps {
  isOpen: boolean;
}

const createEmptyOrganization = (): IAssignOrganizationValue => ({
  organization: null,
  positions: [createEmptyPosition()],
});

const createDefaultValues = (): ILicenseAssignFormValues => ({
  organizations: [createEmptyOrganization()],
});

const LicenseAssignModal = ({ isOpen }: LicenseAssignModalProps) => {
  const { t } = useTranslation();
  const setParams = useURLParamsSetter();
  const { assignLicenses } = useLicenseActionsSubmit();

  const { control, handleSubmit, reset } = useForm<ILicenseAssignFormValues>({
    defaultValues: createDefaultValues(),
  });

  const { fields: organizationFields, append: appendOrganization } =
    useFieldArray({
      control,
      name: "organizations",
    });

  const organizationsValues =
    (useWatch<ILicenseAssignFormValues>({ control })
      .organizations as ILicenseAssignFormValues["organizations"]) || [];
  const isFilled = isAssignFormFilled({
    organizations: organizationsValues,
  });

  const lastOrganization = organizationsValues[organizationsValues.length - 1];
  const canAddOrganization = Boolean(
    lastOrganization && isAssignOrganizationFilled(lastOrganization),
  );

  const closeModal = () => {
    reset(createDefaultValues());
    setParams({ openModal: undefined });
  };

  useEffect(() => {
    if (isOpen) {
      reset(createDefaultValues());
    }
  }, [isOpen, reset]);

  const submit = handleSubmit((data) => {
    assignLicenses.mutate(mapAssignFormToPayload(data), {
      onSuccess: (result) => {
        if (result.assigned > 0) {
          closeModal();
        }
      },
    });
  });

  return (
    <Modal
      open={isOpen}
      onCancel={closeModal}
      title={t("licenseActionsModals.assignTitle")}
      size="small"
      centered
      cancelButton={{
        children: t("licenseActionsModals.cancel"),
        disabled: assignLicenses.isLoading,
        onClick: closeModal,
        bold: true,
        fontSize: "14",
        radius: 5,
      }}
      okButton={{
        children: t("licenseActionsModals.assignSubmit"),
        onClick: submit,
        disabled: !isFilled || assignLicenses.isLoading,
        bold: true,
        fontSize: "14",
        radius: 5,
      }}
    >
      <div className={s.form} key={isOpen ? "open" : "closed"}>
        <div className={s.blocks}>
          {organizationFields.map((organizationField, organizationIndex) => (
            <AssignOrganizationBlock
              key={organizationField.id}
              control={control}
              organizationIndex={organizationIndex}
              includeAllPartners
              value={
                organizationsValues[organizationIndex] ||
                createEmptyOrganization()
              }
            />
          ))}
        </div>
        <div>
          <AddEntityLink
            disabled={!canAddOrganization}
            onClick={() => appendOrganization(createEmptyOrganization())}
          >
            {t("licenseActionsModals.addOrganization")}
          </AddEntityLink>
        </div>
      </div>
    </Modal>
  );
};

export default LicenseAssignModal;
